import { useCallback, useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import clsx from 'clsx'
import {
  ArrowLeft,
  KanbanSquare,
  Loader2,
  PanelLeftClose,
  PanelLeftOpen,
  PanelRightClose,
  PanelRightOpen,
  Plus,
  Trash2,
} from 'lucide-react'

import {
  createShot,
  createTake,
  deleteShot,
  getCollaborationEligibility,
  getProduction,
  listAssets,
  listDepartments,
  listMembers,
  listScriptEpisodes,
  listShots,
  listStoryboard,
  patchShot,
  preferTake,
  uploadProductionAsset,
  type CollaborationEligibility,
  type Production,
  type ProductionAsset,
  type ProductionDepartment,
  type ProductionMember,
  type ScriptScene,
  type Shot,
  type StoryboardFrame,
} from '@/api/studio-production'
import { ConfirmDialog } from '@/shared/components/ConfirmDialog'
import { SceneReferenceAssets } from '@/features/studio/components/SceneReferenceAssets'
import { ShotEditorAIPanel } from '@/features/studio/components/ShotEditorAIPanel'
import {
  StoryboardFrameCell,
  StoryboardInlineCell,
  type ShotEditableField,
} from '@/features/studio/components/storyboard/StoryboardInlineCells'
import { ShotFramePickerModal } from '@/features/studio/components/storyboard/ShotFramePickerModal'
import { ImagePreviewModal } from '@/features/studio/modals/ImagePreviewModal'
import { AssignWorkItemModal, type AssignWorkItemPreset } from '@/features/studio/modals/AssignWorkItemModal'
import { productionBasePath } from '@/features/studio/lib/production-path'
import { canvasPath } from '@/features/studio/lib/canvas-path'
import { fetchProductionScriptEpisode } from '@/features/studio/lib/script-episode-cache'
import { buildShotSceneScopeKey } from '@/features/studio/lib/canvas-scope'
import { resolveCollaborationGate } from '@/features/studio/lib/collaboration-gate'
import { useAuth } from '@/hooks'
import { useAssignmentScopes } from '@/features/studio/hooks/useAssignmentScopes'
import { resolveApiErrorMessage } from '@/features/studio/lib/resolveApiError'
import {
  formatEpisodeLabel,
} from '@/features/studio/script/season'
import {
  CAMERA_ANGLE_OPTIONS,
  CAMERA_MOVEMENT_OPTIONS,
  formatShotSize,
  normalizeShotSize,
  SHOT_SIZE_OPTIONS,
} from '@/features/studio/shot/storyboard-sheet'
import {
  ProtoBtn,
  ProtoIconBtn,
  ProtoPagination,
} from '@/features/studio/components/ui-kit'
import { Button } from '@/shared/components/ui/button'
import { Checkbox } from '@/shared/components/ui/checkbox'
import '@/features/studio/styles/storyboard-excel.css'
import '@/features/studio/styles/script-editor.css'

const SHOT_PAGE_SIZE = 15
const SHOT_FETCH_LIMIT = 500

export function EpisodeShotWorkbenchPage() {
  const { productionId = '', scriptId: scriptIdParam = '', episodeNo: episodeNoParam = '1' } = useParams()
  const scriptId = Number(scriptIdParam)
  const episodeNo = Number(episodeNoParam)
  const { t } = useTranslation()
  const { user } = useAuth()
  const navigate = useNavigate()
  const base = productionBasePath(productionId)

  const [episodeBody, setEpisodeBody] = useState('')
  const [episodeTitle, setEpisodeTitle] = useState('')
  const [episodesPerSeason, setEpisodesPerSeason] = useState(0)
  const [episodeTarget, setEpisodeTarget] = useState(1)
  const [scenes, setScenes] = useState<ScriptScene[]>([])
  const [shots, setShots] = useState<Shot[]>([])
  const [frames, setFrames] = useState<StoryboardFrame[]>([])
  const [page, setPage] = useState(1)
  const [sceneFilter, setSceneFilter] = useState(1)
  const [aiSceneNo, setAiSceneNo] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [editingCell, setEditingCell] = useState<{ shotId: string; field: ShotEditableField } | null>(null)
  const [savingCell, setSavingCell] = useState<{ shotId: string; field: ShotEditableField } | null>(null)
  const [previewUrls, setPreviewUrls] = useState<string[]>([])
  const [previewIndex, setPreviewIndex] = useState(0)
  const [previewTitle, setPreviewTitle] = useState('')
  const [deleteTarget, setDeleteTarget] = useState<Shot | null>(null)
  const [deleteBusy, setDeleteBusy] = useState(false)
  const [assets, setAssets] = useState<ProductionAsset[]>([])
  const [assetsLoading, setAssetsLoading] = useState(false)
  const [framePickerShot, setFramePickerShot] = useState<Shot | null>(null)
  const [production, setProduction] = useState<Production | null>(null)
  const [members, setMembers] = useState<ProductionMember[]>([])
  const [departments, setDepartments] = useState<ProductionDepartment[]>([])
  const [eligibility, setEligibility] = useState<CollaborationEligibility | null>(null)
  const [assignOpen, setAssignOpen] = useState(false)
  const [assignPreset, setAssignPreset] = useState<AssignWorkItemPreset | undefined>()
  const [leftOpen, setLeftOpen] = useState(true)
  const [rightOpen, setRightOpen] = useState(false)
  const [selectedShotIds, setSelectedShotIds] = useState<Set<string>>(() => new Set())

  const gate = useMemo(
    () => resolveCollaborationGate(production, members, eligibility, user?.id),
    [production, members, eligibility, user?.id],
  )

  const isOwner =
    !!production?.is_owner ||
    (production?.user_id != null && user?.id != null && production.user_id === user.id)
  const { canEdit: canEditScope } = useAssignmentScopes(productionId, isOwner)

  const episodeLabel = formatEpisodeLabel(episodeNo, episodesPerSeason, episodeTarget)

  useEffect(() => {
    void (async () => {
      try {
        const [prod, memRes, deptRes, elig] = await Promise.all([
          getProduction(productionId).catch(() => null),
          listMembers(productionId).catch(() => ({ items: [] as ProductionMember[] })),
          listDepartments(productionId).catch(() => ({ items: [] as ProductionDepartment[] })),
          getCollaborationEligibility(productionId).catch(() => null),
        ])
        setProduction(prod)
        setMembers(memRes.items ?? [])
        setDepartments(deptRes.items ?? [])
        setEligibility(elig)
      } catch {
        /* optional collaboration context */
      }
    })()
  }, [productionId])

  const reload = useCallback(async () => {
    setLoading(true)
    setError(null)
    try {
      const [scriptRes, epMeta, frameRes] = await Promise.all([
        fetchProductionScriptEpisode(productionId, scriptId, episodeNo),
        listScriptEpisodes(productionId, scriptId).catch(() => null),
        listStoryboard(productionId, episodeNo).catch(() => ({ items: [] as StoryboardFrame[] })),
      ])
      const scenesLoaded = scriptRes.scenes ?? []
      const shotRes = await listShots(productionId, {
        script_id: scriptId,
        episode_no: episodeNo,
        limit: SHOT_FETCH_LIMIT,
        offset: 0,
      })
      setEpisodeBody(scriptRes.body ?? '')
      setEpisodeTitle(scriptRes.title ?? '')
      setEpisodesPerSeason(epMeta?.episodes_per_season ?? 0)
      setEpisodeTarget(epMeta?.episode_target ?? 1)
      setScenes(scenesLoaded)
      setShots(shotRes.items ?? [])
      setFrames((frameRes.items ?? []).filter((f) => f.episode_no === episodeNo))
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'shots.loadFailed'))
    } finally {
      setLoading(false)
    }
  }, [productionId, scriptId, episodeNo])

  useEffect(() => {
    void reload()
  }, [reload])

  const shotsByScene = useMemo(() => {
    const map = new Map<number, number>()
    for (const s of shots ?? []) {
      map.set(s.scene_no, (map.get(s.scene_no) ?? 0) + 1)
    }
    return map
  }, [shots])

  useEffect(() => {
    if (scenes.length === 0) return
    const valid = scenes.some((s) => s.scene_no === sceneFilter)
    const next = valid ? sceneFilter : scenes[0].scene_no
    if (!valid) {
      setSceneFilter(next)
      setPage(1)
    }
    if (aiSceneNo !== next) setAiSceneNo(next)
  }, [scenes, sceneFilter, aiSceneNo])

  useEffect(() => {
    setPage(1)
  }, [sceneFilter])

  useEffect(() => {
    setSelectedShotIds(new Set())
  }, [sceneFilter, page])

  const frameByShotId = useMemo(() => {
    const map = new Map<string, string>()
    for (const f of frames) {
      if (f.preview_url) map.set(f.public_id, f.preview_url)
    }
    return map
  }, [frames])

  const sceneHeadingByNo = useMemo(() => {
    const map = new Map<number, string>()
    for (const sc of scenes) {
      if (sc.heading?.trim()) map.set(sc.scene_no, sc.heading.trim())
    }
    return map
  }, [scenes])

  function openAssign(preset: AssignWorkItemPreset) {
    setAssignPreset(preset)
    setAssignOpen(true)
  }

  function sceneScopeKey(sceneNo: number) {
    return buildShotSceneScopeKey(scriptId, episodeNo, sceneNo)
  }

  const canEditShot = useCallback(
    (shotPublicId: string) => canEditScope('shot', shotPublicId),
    [canEditScope],
  )

  const canEditScene = useCallback(
    (sceneNo: number) => canEditScope('shot_scene', sceneScopeKey(sceneNo)),
    [canEditScope, scriptId, episodeNo],
  )

  const filteredShots = useMemo(() => {
    const seen = new Set<string>()
    return (shots ?? []).filter((s) => {
      if (s.scene_no !== sceneFilter) return false
      const slotKey = `${s.scene_no}:${s.shot_no}:${s.slug}:${s.public_id}`
      if (seen.has(slotKey)) return false
      seen.add(slotKey)
      return true
    })
  }, [shots, sceneFilter])

  const pagedShots = useMemo(() => {
    const start = (page - 1) * SHOT_PAGE_SIZE
    return filteredShots.slice(start, start + SHOT_PAGE_SIZE)
  }, [filteredShots, page])

  const tableSceneLabel = useMemo(() => {
    const heading = sceneHeadingByNo.get(sceneFilter)
    const code = `S${String(sceneFilter).padStart(2, '0')}`
    return heading ? `${code} · ${heading}` : code
  }, [sceneFilter, sceneHeadingByNo])

  const tableShotCount = filteredShots.length

  const canvasReturnPath = `${base}/shots/${scriptId}/episodes/${episodeNo}`

  const allVisibleSelected =
    pagedShots.length > 0 && pagedShots.every((s) => selectedShotIds.has(s.public_id))
  const someVisibleSelected = pagedShots.some((s) => selectedShotIds.has(s.public_id))

  function toggleShotSelection(shotId: string) {
    setSelectedShotIds((prev) => {
      const next = new Set(prev)
      if (next.has(shotId)) next.delete(shotId)
      else next.add(shotId)
      return next
    })
  }

  function toggleSelectAllVisible() {
    setSelectedShotIds((prev) => {
      const next = new Set(prev)
      if (allVisibleSelected) {
        for (const shot of pagedShots) next.delete(shot.public_id)
      } else {
        for (const shot of pagedShots) next.add(shot.public_id)
      }
      return next
    })
  }

  function handleOpenCanvasGenerate() {
    navigate(
      canvasPath(productionId, {
        ctx: 'shot-frame',
        return: canvasReturnPath,
        label: tableSceneLabel,
        scriptId,
        episodeNo,
        sceneNo: sceneFilter,
        ...(selectedShotIds.size > 0 ? { shotIds: Array.from(selectedShotIds) } : undefined),
      }),
    )
  }

  async function handleCreateShot(sceneNo = sceneFilter) {
    try {
      await createShot(productionId, { script_id: scriptId, episode_no: episodeNo, scene_no: sceneNo })
      await reload()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'shots.createFailed'))
    }
  }

  async function handleInlineSave(shot: Shot, field: ShotEditableField, value: string | number) {
    setEditingCell(null)
    setSavingCell({ shotId: shot.public_id, field })
    const payload: Record<string, string | number> = {}
    if (field === 'duration_sec') {
      payload.duration_sec = typeof value === 'number' ? value : Number(value) || shot.duration_sec
    } else if (field === 'shot_size') {
      payload.shot_size = String(value)
    } else {
      payload[field] = String(value)
    }
    try {
      await patchShot(productionId, shot.public_id, payload)
      setShots((prev) =>
        (prev ?? []).map((s) => (s.public_id === shot.public_id ? { ...s, ...payload } : s)),
      )
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'shots.saveFailed'))
    } finally {
      setSavingCell(null)
    }
  }

  async function handleDelete() {
    if (!deleteTarget) return
    setDeleteBusy(true)
    try {
      await deleteShot(productionId, deleteTarget.public_id)
      setDeleteTarget(null)
      await reload()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'shots.deleteFailed'))
    } finally {
      setDeleteBusy(false)
    }
  }

  async function openFramePicker(shot: Shot) {
    setFramePickerShot(shot)
    setAssetsLoading(true)
    try {
      const res = await listAssets(productionId, { kind: 'storyboard' })
      setAssets(res.items ?? [])
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'shots.assetLoadFailed'))
    } finally {
      setAssetsLoading(false)
    }
  }

  async function attachAssetToShot(shot: Shot, url: string) {
    try {
      const take = await createTake(productionId, shot.public_id, {
        media_url: url,
        media_kind: 'image',
        status: 'succeeded',
      })
      await preferTake(productionId, shot.public_id, take.id)
      setFramePickerShot(null)
      await reload()
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'shots.attachFailed'))
    }
  }

  async function handleUploadForShot(shot: Shot, file: File) {
    try {
      const asset = await uploadProductionAsset(productionId, file, {
        kind: 'storyboard',
        name: `${shot.slug}-ref`,
        shot_public_id: shot.public_id,
        shot_scene_key: sceneScopeKey(shot.scene_no),
      })
      await attachAssetToShot(shot, asset.media_url ?? '')
    } catch (e) {
      setError(resolveApiErrorMessage(e, 'shots.uploadFailed'))
    }
  }

  if (loading && shots.length === 0 && !episodeBody) {
    return (
      <div className="script-editor-shell fixed inset-0 z-50 flex items-center justify-center bg-[#0a0a12]">
        <Loader2 className="h-8 w-8 animate-spin text-indigo-400" />
      </div>
    )
  }

  return (
    <div className="script-editor-shell fixed inset-0 z-50 flex flex-col overflow-hidden bg-[#0a0a12]">
      <header className="flex shrink-0 flex-wrap items-center gap-3 border-b border-white/6 px-4 py-3 sm:px-6">
        <Link
          to={`${base}/shots`}
          className="inline-flex items-center gap-1 text-xs text-zinc-500 hover:text-zinc-300"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          {t('shots.backList')}
        </Link>
        <div className="min-w-0 flex-1">
          <h1 className="truncate text-sm font-semibold text-white">
            {t('shots.episodeWorkbenchTitle', { label: episodeLabel })}
          </h1>
          <p className="truncate text-xs text-zinc-500">
            {episodeTitle || t('shots.episodeWorkbenchSubtitle')}
          </p>
        </div>
        {canEditScene(sceneFilter) ? (
          <ProtoBtn size="sm" onClick={() => void handleCreateShot(sceneFilter)}>
            <Plus className="h-3.5 w-3.5" />
            {t('shots.add')}
          </ProtoBtn>
        ) : null}
      </header>

      {error ? <p className="px-4 py-2 text-xs text-rose-300">{t(error, { defaultValue: error })}</p> : null}

      <div className="flex min-h-0 flex-1 overflow-hidden">
        {leftOpen ? (
          <aside className="studio-scrollbar-thin relative w-64 shrink-0 overflow-y-auto border-r border-white/[0.06] bg-[#0a0a12] p-3">
            <div className="mb-2 flex items-center justify-between gap-1 pr-0.5">
              <p className="text-[10px] font-semibold uppercase tracking-widest text-zinc-600">
                {t('shots.sceneNav')}
              </p>
              <ProtoIconBtn
                className="!p-1"
                aria-label={t('shots.collapseScenePanel')}
                onClick={() => setLeftOpen(false)}
              >
                <PanelLeftClose className="h-4 w-4" />
              </ProtoIconBtn>
            </div>
          {scenes.map((sc) => {
            const selected = sceneFilter === sc.scene_no
            return (
              <div key={sc.scene_no} className="mb-2">
                <Button
                  type="button"
                  variant="ghost"
                  onClick={() => {
                    setSceneFilter(sc.scene_no)
                    setAiSceneNo(sc.scene_no)
                    setPage(1)
                  }}
                  className={clsx(
                    'h-auto w-full justify-start rounded-lg px-2.5 py-2 text-left text-xs font-normal',
                    selected ? 'bg-indigo-500/15 text-indigo-200 hover:bg-indigo-500/15' : 'text-zinc-400 hover:bg-white/[0.04]',
                  )}
                >
                  <span className="font-mono text-indigo-300/80">S{String(sc.scene_no).padStart(2, '0')}</span>
                  <span className="ml-1 text-zinc-600">({shotsByScene.get(sc.scene_no) ?? 0})</span>
                  <p className="mt-0.5 line-clamp-2 text-zinc-500">{sc.heading || '—'}</p>
                </Button>
                <div className="mt-1.5 px-1">
                  <p className="mb-1 text-[9px] font-medium uppercase tracking-wide text-zinc-600">
                    {t('shots.sceneReferences')}
                  </p>
                  <SceneReferenceAssets
                    productionId={productionId}
                    scriptId={scriptId}
                    episodeNo={episodeNo}
                    sceneNo={sc.scene_no}
                    scopeKey={sceneScopeKey(sc.scene_no)}
                    storyboardUrl={sc.storyboard_url}
                    canEdit={canEditScene(sc.scene_no)}
                    compact
                    onStoryboardChange={(url) =>
                      setScenes((prev) =>
                        prev.map((row) =>
                          row.scene_no === sc.scene_no ? { ...row, storyboard_url: url } : row,
                        ),
                      )
                    }
                    onPreview={(urls, index, title) => {
                      setPreviewUrls(urls)
                      setPreviewIndex(index)
                      setPreviewTitle(title)
                    }}
                  />
                  {gate.canAssignWork ? (
                    <ProtoBtn
                      size="xs"
                      variant="soft"
                      tone="sky"
                      className="mt-1.5 w-full"
                      onClick={() =>
                        openAssign({
                          shotSceneKey: sceneScopeKey(sc.scene_no),
                          expectedKind: 'storyboard',
                          title: `${t('shots.assignScene')} · S${String(sc.scene_no).padStart(2, '0')}`,
                        })
                      }
                    >
                      <KanbanSquare className="h-3 w-3" />
                      {t('shots.assignScene')}
                    </ProtoBtn>
                  ) : null}
                </div>
              </div>
            )
          })}
          {scenes.length === 0 ? (
            <p className="px-2 text-xs text-zinc-600">{t('shots.noScenes')}</p>
          ) : null}
          </aside>
        ) : (
          <div className="flex w-9 shrink-0 flex-col items-center border-r border-white/[0.06] bg-[#0a0a12] pt-2">
            <ProtoIconBtn aria-label={t('shots.expandScenePanel')} onClick={() => setLeftOpen(true)}>
              <PanelLeftOpen className="h-4 w-4" />
            </ProtoIconBtn>
          </div>
        )}

        <div className="flex min-h-0 min-w-0 flex-1 flex-col overflow-hidden">
          <div className="flex shrink-0 flex-wrap items-center justify-between gap-3 border-b border-white/[0.06] px-4 py-3">
            <div className="flex min-w-0 flex-1 items-baseline gap-2">
              <p className="shrink-0 text-xs font-semibold tracking-wide text-zinc-400">
                {t('shots.professionalTableTitle')}
              </p>
              <p className="truncate text-xs text-zinc-500">{tableSceneLabel}</p>
            </div>
            <div className="flex shrink-0 flex-wrap items-center gap-3">
              <ProtoBtn
                size="sm"
                variant="secondary"
                onClick={handleOpenCanvasGenerate}
              >
                {t('shots.openCanvasGenerate')}
                {selectedShotIds.size > 0
                  ? t('shots.canvasSelectedCount', { n: selectedShotIds.size })
                  : null}
              </ProtoBtn>
              <span className="text-[10px] text-zinc-600">
                {t('shots.tableRowCount', { n: tableShotCount })}
              </span>
              {tableShotCount === 0 && scenes.length > 0 ? (
                <span className="text-[10px] text-zinc-600">{t('shots.workbenchEmpty')}</span>
              ) : null}
            </div>
          </div>

          <div className="flex min-h-0 flex-1 flex-col gap-3 px-4 py-3">
            <div className="storyboard-excel-sheet min-h-0 flex-1">
              <div className="storyboard-excel-scroll studio-scrollbar-thin">
                <table>
                  <colgroup>
                    <col style={{ width: 40 }} />
                    <col style={{ width: 108 }} />
                    <col style={{ width: 56 }} />
                    <col style={{ width: 56 }} />
                    <col style={{ width: 56 }} />
                    <col style={{ width: 260 }} />
                    <col style={{ width: 180 }} />
                    <col style={{ width: 120 }} />
                    <col style={{ width: 100 }} />
                    <col style={{ width: 52 }} />
                    <col style={{ width: 140 }} />
                    <col style={{ width: 100 }} />
                    <col style={{ width: 96 }} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th className="storyboard-excel-select">
                        <Checkbox
                          checked={allVisibleSelected ? true : someVisibleSelected ? 'indeterminate' : false}
                          onCheckedChange={() => toggleSelectAllVisible()}
                          aria-label={t('shots.selectAllVisible')}
                          disabled={pagedShots.length === 0}
                          className="border-zinc-600 data-checked:border-indigo-500 data-checked:bg-indigo-500"
                        />
                      </th>
                      <th>{t('shots.col.slug')}</th>
                      <th>{t('shots.col.shotSize')}</th>
                      <th>{t('shots.col.cameraAngle')}</th>
                      <th>{t('shots.col.cameraMovement')}</th>
                      <th>{t('shots.col.description')}</th>
                      <th>{t('shots.col.dialogue')}</th>
                      <th>{t('shots.col.soundNote')}</th>
                      <th>{t('shots.col.musicNote')}</th>
                      <th>{t('shots.col.duration')}</th>
                      <th>{t('shots.col.storyboardNote')}</th>
                      <th>{t('shots.col.frame')}</th>
                      <th className="storyboard-excel-actions">{t('shots.col.action')}</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pagedShots.map((shot) => {
                      const preview = frameByShotId.get(shot.public_id)
                      const editable = canEditShot(shot.public_id)
                      const inlineProps = (field: ShotEditableField) => ({
                        field,
                        canEdit: editable,
                        isEditing: editingCell?.shotId === shot.public_id && editingCell.field === field,
                        saving: savingCell?.shotId === shot.public_id && savingCell.field === field,
                        onStartEdit: () => setEditingCell({ shotId: shot.public_id, field }),
                        onCancel: () => setEditingCell(null),
                        onSave: (value: string | number) => void handleInlineSave(shot, field, value),
                      })
                      return (
                        <tr key={shot.public_id}>
                          <td className="storyboard-excel-select">
                            <Checkbox
                              checked={selectedShotIds.has(shot.public_id)}
                              onCheckedChange={() => toggleShotSelection(shot.public_id)}
                              aria-label={t('shots.selectShot', { slug: shot.slug })}
                              className="border-zinc-600 data-checked:border-indigo-500 data-checked:bg-indigo-500"
                            />
                          </td>
                          <td className="cell-mono text-indigo-200">{shot.slug}</td>
                          <StoryboardInlineCell
                            {...inlineProps('shot_size')}
                            display={formatShotSize(shot.shot_size)}
                            rawValue={normalizeShotSize(shot.shot_size)}
                            center
                            options={SHOT_SIZE_OPTIONS}
                          />
                          <StoryboardInlineCell
                            {...inlineProps('camera_angle')}
                            display={shot.camera_angle}
                            rawValue={shot.camera_angle}
                            center
                            options={CAMERA_ANGLE_OPTIONS}
                          />
                          <StoryboardInlineCell
                            {...inlineProps('camera_movement')}
                            display={shot.camera_movement}
                            rawValue={shot.camera_movement}
                            center
                            options={CAMERA_MOVEMENT_OPTIONS}
                          />
                          <StoryboardInlineCell
                            {...inlineProps('description')}
                            display={shot.description}
                            rawValue={shot.description}
                            multiline
                            placeholder={t('shots.descriptionPlaceholder')}
                          />
                          <StoryboardInlineCell
                            {...inlineProps('dialogue')}
                            display={shot.dialogue}
                            rawValue={shot.dialogue}
                            multiline
                            placeholder={t('shots.dialoguePlaceholder')}
                          />
                          <StoryboardInlineCell
                            {...inlineProps('sound_note')}
                            display={shot.sound_note}
                            rawValue={shot.sound_note}
                            placeholder={t('shots.soundNotePlaceholder')}
                          />
                          <StoryboardInlineCell
                            {...inlineProps('music_note')}
                            display={shot.music_note}
                            rawValue={shot.music_note}
                            placeholder={t('shots.musicNotePlaceholder')}
                          />
                          <StoryboardInlineCell
                            {...inlineProps('duration_sec')}
                            display={`${shot.duration_sec}s`}
                            rawValue={shot.duration_sec}
                            center
                          />
                          <StoryboardInlineCell
                            {...inlineProps('storyboard_note')}
                            display={shot.storyboard_note}
                            rawValue={shot.storyboard_note}
                            multiline
                            placeholder={t('shots.storyboardNotePlaceholder')}
                          />
                          <StoryboardFrameCell
                            preview={preview}
                            canEdit={editable}
                            onPreview={() => {
                              if (!preview) return
                              setPreviewUrls([preview])
                              setPreviewIndex(0)
                              setPreviewTitle(shot.slug)
                            }}
                            onPick={() => void openFramePicker(shot)}
                          />
                          <td className="storyboard-excel-actions">
                            {(isOwner || editable) ? (
                              <ProtoBtn
                                size="xs"
                                variant="ghost"
                                className="!rounded-none text-zinc-500 hover:text-rose-300"
                                onClick={() => setDeleteTarget(shot)}
                              >
                                <Trash2 className="h-3 w-3" />
                                {t('common.delete')}
                              </ProtoBtn>
                            ) : (
                              <span className="cell-empty">—</span>
                            )}
                          </td>
                        </tr>
                      )
                    })}
                  </tbody>
                </table>
              </div>
            </div>

            {filteredShots.length === 0 ? (
              <p className="py-8 text-center text-sm text-zinc-600">
                {scenes.length > 0 ? t('shots.workbenchEmpty') : t('shots.empty')}
              </p>
            ) : null}
          </div>

          <div className="shrink-0 border-t border-white/[0.06] px-4 py-2">
            <ProtoPagination
              page={page}
              pageSize={SHOT_PAGE_SIZE}
              total={filteredShots.length}
              onPageChange={setPage}
              prevLabel={t('pagination.prev')}
              nextLabel={t('pagination.next')}
            />
          </div>
        </div>

        {rightOpen ? (
          <aside className="studio-scrollbar-hidden flex w-80 shrink-0 flex-col overflow-hidden border-l border-white/[0.06] bg-[#0a0a12]">
            <div className="flex shrink-0 items-center justify-end border-b border-white/[0.06] px-2 py-1.5">
              <ProtoIconBtn
                className="!p-1"
                aria-label={t('shots.collapseAiPanel')}
                onClick={() => setRightOpen(false)}
              >
                <PanelRightClose className="h-4 w-4" />
              </ProtoIconBtn>
            </div>
            <ShotEditorAIPanel
              productionId={productionId}
              scriptId={scriptId}
              episodeNo={episodeNo}
              scenes={scenes}
              selectedSceneNo={aiSceneNo}
              onSceneChange={(no) => {
                setAiSceneNo(no)
                setSceneFilter(no)
                setPage(1)
              }}
              onApplied={() => void reload()}
            />
          </aside>
        ) : (
          <div className="flex w-9 shrink-0 flex-col items-center border-l border-white/[0.06] bg-[#0a0a12] pt-2">
            <ProtoIconBtn aria-label={t('shots.expandAiPanel')} onClick={() => setRightOpen(true)}>
              <PanelRightOpen className="h-4 w-4" />
            </ProtoIconBtn>
          </div>
        )}
      </div>

      <ImagePreviewModal
        open={previewUrls.length > 0}
        urls={previewUrls}
        index={previewIndex}
        title={previewTitle}
        onClose={() => {
          setPreviewUrls([])
          setPreviewIndex(0)
          setPreviewTitle('')
        }}
        onIndexChange={setPreviewIndex}
      />

      {framePickerShot ? (
        <ShotFramePickerModal
          shot={framePickerShot}
          assets={assets}
          assetsLoading={assetsLoading}
          onClose={() => setFramePickerShot(null)}
          onUpload={(file) => void handleUploadForShot(framePickerShot, file)}
          onPickAsset={(url) => void attachAssetToShot(framePickerShot, url)}
        />
      ) : null}

      <ConfirmDialog
        open={!!deleteTarget}
        title={t('shots.deleteTitle')}
        message={t('shots.deleteConfirm', { slug: deleteTarget?.slug ?? '' })}
        destructive
        busy={deleteBusy}
        confirmLabel={t('common.delete')}
        cancelLabel={t('common.cancel')}
        onConfirm={() => void handleDelete()}
        onCancel={() => setDeleteTarget(null)}
      />

      <AssignWorkItemModal
        open={assignOpen}
        onClose={() => setAssignOpen(false)}
        productionId={productionId}
        members={members}
        departments={departments}
        preset={assignPreset}
        gate={gate}
      />
    </div>
  )
}
